import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, finalize, catchError, of } from 'rxjs';
import { ServicecallService } from 'src/app/services';

/**
 * Interface defining site data structure
 */
export interface SiteData {
  _id: string;
  name: string;
  about?: string;
  image?: string;
  status?: 'active' | 'inactive' | 'maintenance';
  endDate?: string;
}

/**
 * Component that displays site tiles in a grid with pagination
 * Emits selected site ID when a tile is clicked
 */
@Component({
  selector: 'app-site-tile',
  templateUrl: './site-tile.component.html',
  styleUrls: ['./site-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimize change detection
})
export class SiteTileComponent implements OnInit, OnDestroy {
  @Output() siteSelected = new EventEmitter<string>();

  public siteList: SiteData[] = [];

  /** pagination */
  public currentPage: number = 1;
  public totalPages: number[] = [];
  endPage: boolean = false;

  /** Error message if API call fails */
  public errorMessage: string | null = null;

  /** Subscription for site list API call */
  private _sitesListSubscription: Subscription | null = null;

  /**
   * Constructor with dependency injection
   * @param _router Angular router service
   * @param _service API service for data fetching
   * @param _cdr ChangeDetectorRef for manual change detection
   */
  constructor(
    private _router: Router,
    private _service: ServicecallService,
    private _cdr: ChangeDetectorRef
  ) {}

  /**
   * Lifecycle hook for component initialization
   */
  ngOnInit(): void {
    this._fetchSiteData();
  }

  /**
   * Lifecycle hook for cleanup on component destruction
   */
  ngOnDestroy(): void {
    if (this._sitesListSubscription) {
      this._sitesListSubscription.unsubscribe();
      this._sitesListSubscription = null;
    }
  }

  /**
   * Fetches site data from the service
   * Updates component state with the results
   */
  private _fetchSiteData(): void {
    this.errorMessage = null;
    this._cdr.markForCheck();
    // Cancel any existing subscription
    if (this._sitesListSubscription) {
      this._sitesListSubscription.unsubscribe();
    }
    // Make API call with current page parameter
    this._sitesListSubscription = this._service
      .plantList(this.currentPage, '', true)
      .pipe(
        // Handle cleanup when observable completes
        finalize(() => {
          this._cdr.markForCheck();
        }),
        // Handle errors gracefully
        catchError((error) => {
          this.errorMessage = 'Failed to load sites. Please try again later.';
          console.error('Site data fetch error:', error);
          return of(null); // Return empty observable to continue execution
        })
      )
      .subscribe((result: any) => {
        if (!result) {
          this._cdr.markForCheck();
          return;
        }
        if (result['status']) {
          const data = result['response']['data'];
          if (data?.length > 0) {
            this.siteList = data;
            // Update status based on end date if present
            this.siteList = this.siteList.map((item: SiteData) => {
              if (item?.endDate && new Date(item.endDate) < new Date()) {
                return { ...item, status: 'inactive' };
              } else {
                return { ...item, status: 'active' };
              }
            });
          } else {
            this.siteList = [];
          }
          // Process pagination information
          if (result['response']['pageInfo']?.pages) {
            this.totalPages = result.response.pageInfo.pages;
            this.endPage = result?.response?.pageInfo?.endPage;
          } else {
            this.totalPages = [];
            this.endPage = false;
          }
        } else {
          // Handle failed response status
          this.siteList = [];
          this.errorMessage = result.message || 'Failed to load sites data.';
        }
        this._cdr.markForCheck();
      });
  }

  /**
   * Handles site tile click events
   * @param siteId ID of the clicked site
   */
  onSiteClick(siteId: string): void {
    this.siteSelected.emit(siteId);
    // this._router.navigate(['/site', siteId]);
  }

  /**
   * Maps status string to CSS class
   * @param status Site status string
   * @returns CSS class corresponding to the status
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'inactive':
        return 'status-inactive';
      case 'maintenance':
        return 'status-maintenance';
      default:
        return '';
    }
  }

  /**
   * Handles pagination change events
   * @param page New page number
   */
  onPaginateChange(page: number): void {
    if (this.currentPage !== page) {
      this.currentPage = page;
      const scrollElement = document.getElementById('launchScroll');
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'smooth' });
      }
      // Fetch data for the new page
      this._fetchSiteData();
    }
  }

  /**
   * Handles image loading errors by setting a fallback image
   * @param event The error event from the image element
   */
  handleImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement) {
      imgElement.src = 'assets/images/mockFacility.svg';
    }
  }
}
